import type { ProblemBody, Rng, SkillDef } from "../types";
import { fmt, prob } from "../util";

/** Target seconds per level; multi-step problems are a fixed shape per level. */
const T = [5, 6, 8, 10, 11, 9, 14, 20, 12, 15];

function gen(L: number, r: Rng): ProblemBody {
  let a: number, b: number, c: number, d: number;
  const t = T[L - 1];
  switch (L) {
    case 1: a = r.int(2, 20); b = r.int(2, 9); c = r.int(2, 9);
      return prob(`${a} + ${b} × ${c}`, a + b * c, `× first: ${b}×${c} = ${b * c}; ${a} + ${b * c} = ${a + b * c}`, { t });
    case 2: a = r.int(2, 30); b = r.int(2, 30); c = r.int(2, 9);
      return prob(`(${a} + ${b}) × ${c}`, (a + b) * c, `${a} + ${b} = ${a + b}; ×${c} = ${(a + b) * c}`, { t });
    case 3: a = r.int(12, 99); b = r.int(2, 9); c = r.int(1, a * b - 1);
      return prob(`${a} × ${b} − ${c}`, a * b - c, `${a}×${b} = ${a * b}; − ${c} = ${a * b - c}`, { t });
    case 4: a = r.int(12, 99); b = r.int(2, 9); c = r.int(12, 99); d = r.int(2, 9);
      return prob(`${a} × ${b} + ${c} × ${d}`, a * b + c * d, `${a}×${b} = ${a * b}; ${c}×${d} = ${c * d}; sum = ${fmt(a * b + c * d)}`, { t });
    case 5: a = r.int(20, 99); b = r.int(2, a - 1); c = r.int(2, 20); d = r.int(2, 20);
      return prob(`(${a} − ${b}) × (${c} + ${d})`, (a - b) * (c + d), `${a}−${b} = ${a - b}; ${c}+${d} = ${c + d}; ${a - b}×${c + d} = ${fmt((a - b) * (c + d))}`, { t });
    case 6: { c = r.int(2, 9); const k = r.int(3, 25); a = c * k; b = r.int(3, 19);
      return prob(`${a} × ${b} ÷ ${c}`, k * b, `Divide first: ${a}÷${c} = ${k}; ×${b} = ${k * b}`, { t }); }
    case 7: a = r.int(11, 40); b = r.int(11, 40);
      return prob(`${a}² + ${b}²`, a * a + b * b, `${a}² = ${a * a}; ${b}² = ${b * b}; sum = ${fmt(a * a + b * b)}`, { t });
    case 8: a = r.int(11, 30); b = r.int(11, 30); c = r.int(11, 30); d = r.int(11, 30);
      if (a * b < c * d) [a, b, c, d] = [c, d, a, b];
      return prob(`${a} × ${b} − ${c} × ${d}`, a * b - c * d, `${a}×${b} = ${a * b}; ${c}×${d} = ${c * d}; difference = ${a * b - c * d}`, { t });
    case 9: a = r.int(12, 99); b = r.int(2, 9); c = r.int(2, 9);
      return prob(`${a} × ${b} × ${c}`, a * b * c, `Small ones first: ${b}×${c} = ${b * c}; ${a}×${b * c} = ${fmt(a * b * c)}`, { t });
    default: {
      d = r.int(3, 12); const q = r.int(11, 60), total = d * q; b = r.int(2, 9);
      a = r.int(2, Math.max(2, Math.floor((total - 1) / b))); c = total - a * b;
      if (c < 1) { a = 1; c = total - b; }
      return prob(`(${a} × ${b} + ${c}) ÷ ${d}`, q, `${a}×${b} = ${a * b}; + ${c} = ${total}; ÷${d} = ${q}`, { t });
    }
  }
}

export const multi: SkillDef = { key: "multi", name: "Multi-step", blurb: "order of operations", start: 1, generate: gen };
